import React, { useMemo, useState } from "react";
import axios from "axios";
import ChannelManagementPanel from "@/components/ChannelManagementPanel";
import YouTubeIngest from "@/components/upload/YouTubeIngest";
import ArchiveIngest from "@/components/upload/ArchiveIngest";
import MegaIngest from "@/components/upload/MegaIngest";
import MediaManagementPanel from "@/components/upload/MediaManagementPanel";
import StorageMeter from "@/components/upload/StorageMeter";
import PlaylistPanel from "@/components/upload/PlaylistPanel";

const term = { fontFamily: "JetBrains Mono, monospace" };

const WORKSPACES = [
  {
    id: "overview",
    label: "OVERVIEW",
    hint: "Station programming overview, quick status and shortcuts into the main VCR workspaces.",
  },
  {
    id: "channels",
    label: "CHANNELS",
    hint: "Create, select, rename, reorder and configure channels, artwork, descriptions and broadcast state.",
  },
  {
    id: "ingest",
    label: "INGEST",
    hint: "Add tapes manually from MEGA, YouTube or Internet Archive without leaving the selected channel.",
  },
  {
    id: "media",
    label: "MEDIA",
    hint: "Automatic S4 TV/movie ingest status, direct movie upload, review queues and exceptional repair tools.",
  },
  {
    id: "playlist",
    label: "PLAYLIST",
    hint: "Review and maintain every tape on the selected channel, including bulk edit, move, tagging, TMDb and cleanup tools.",
  },
];

function Hint({ text, label = "?" }) {
  return (
    <span className="relative inline-flex group/hint align-middle">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] text-[8px] opacity-55 group-hover/hint:opacity-100 group-hover/hint:border-[var(--phosphor)] transition-all cursor-help"
        aria-label={text}
      >
        {label}
      </span>
      <span
        className="pointer-events-none absolute left-1/2 top-full z-[80] mt-2 hidden w-72 -translate-x-1/2 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black/95 px-3 py-2 text-[10px] leading-relaxed tracking-normal text-[var(--phosphor)] shadow-[0_0_18px_rgba(0,0,0,0.8)] group-hover/hint:block"
        role="tooltip"
      >
        {text}
      </span>
    </span>
  );
}

function StatCard({ eyebrow, value, detail, onClick, hint, testId }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={hint || detail}
      data-testid={testId}
      className={`min-w-0 border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_3%,transparent)] p-3 text-left transition-all ${
        onClick
          ? "hover:border-[color-mix(in_srgb,var(--phosphor)_62%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_7%,transparent)] cursor-pointer"
          : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[9px] tracking-[0.22em] opacity-50 truncate">{eyebrow}</div>
        {hint && <Hint text={hint} />}
      </div>
      <div
        className="text-[26px] md:text-[30px] tracking-[0.08em] leading-none truncate"
        style={{ fontFamily: "VT323, monospace" }}
      >
        {value}
      </div>
      <div className="text-[10px] opacity-60 mt-1.5 leading-snug line-clamp-2">{detail}</div>
    </Wrapper>
  );
}

function WorkspaceHeader({ title, eyebrow, description, hint }) {
  return (
    <div className="border-b border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] pb-3 mb-3">
      <div className="flex items-center gap-2 text-[9px] tracking-[0.26em] opacity-45 mb-1">
        {eyebrow}
        {hint && <Hint text={hint} />}
      </div>
      <div
        className="text-[24px] md:text-[30px] tracking-[0.12em] leading-none"
        style={{ fontFamily: "VT323, monospace" }}
      >
        {title}
      </div>
      <div className="text-[10px] md:text-[11px] opacity-65 mt-1.5 max-w-4xl leading-relaxed">
        {description}
      </div>
    </div>
  );
}

export default function UploadTerminal({
  channels,
  categories,
  videosByChannel,
  currentChannelId,
  onClose,
  onUploaded,
  onChannelCreated,
  onTuneToChannel,
  apiBase,
  authHeaders,
}) {
  const [selectedChannel, setSelectedChannel] = useState(
    currentChannelId || (channels[0] && channels[0].id)
  );
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(["> SYSTEM READY", "> AWAITING INGEST..."]);
  const [newChannel, setNewChannel] = useState("");
  const [workspace, setWorkspace] = useState("overview");
  const [terminalOpen, setTerminalOpen] = useState(false);

  const append = (line) => setLog((l) => [...l, line]);

  const handleCreateChannel = async () => {
    if (!newChannel.trim()) return;
    setBusy(true);
    append(`> CREATING CHANNEL "${newChannel}" ...`);
    try {
      const res = await axios.post(
        `${apiBase}/channels`,
        { name: newChannel.trim() },
        { headers: authHeaders() }
      );
      append(`> CHANNEL ${res.data.number} ALLOCATED.`);
      setNewChannel("");
      setSelectedChannel(res.data.id);
      await onChannelCreated();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const channelVideos = videosByChannel[selectedChannel] || [];
  const selectedChannelRow = channels.find((c) => c.id === selectedChannel) || null;

  const realChannels = useMemo(
    () => channels.filter((c) => !c.virtual && !c.isSubChannel),
    [channels]
  );

  const totalTapes = useMemo(
    () => Object.values(videosByChannel || {}).reduce(
      (total, rows) => total + (Array.isArray(rows) ? rows.length : 0),
      0
    ),
    [videosByChannel]
  );

  const latestLog = log[log.length - 1] || "> SYSTEM READY";
  const activeWorkspace = WORKSPACES.find((item) => item.id === workspace) || WORKSPACES[0];

  const commonPanelProps = {
    apiBase,
    authHeaders,
    channels,
    selectedChannel,
    busy,
    setBusy,
    append,
    onUploaded,
    onTuneToChannel,
  };

  const renderWorkspace = () => {
    if (workspace === "channels") {
      return (
        <>
          <WorkspaceHeader
            eyebrow="LINEUP MANAGEMENT"
            title="CHANNELS"
            description="Keep channel identity, ordering, artwork and broadcast state together in one focused workspace."
            hint="Select a channel here once and the same selection follows you into INGEST, MEDIA and PLAYLIST."
          />
          <div className="w-full max-w-none">
            <ChannelManagementPanel
              channels={channels}
              categories={categories}
              selectedChannel={selectedChannel}
              setSelectedChannel={setSelectedChannel}
              apiBase={apiBase}
              authHeaders={authHeaders}
              busy={busy}
              setBusy={setBusy}
              append={append}
              onChannelsChanged={onChannelCreated}
              channelVideoCount={channelVideos.length}
              videosByChannel={videosByChannel}
            />

            <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-3 bg-black/25">
              <div className="flex items-center gap-2 opacity-80 mb-2 text-[10px] tracking-[0.18em]">
                NEW CHANNEL
                <Hint text="Create a new empty channel. It becomes the active selection immediately so you can configure or ingest into it next." />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newChannel}
                  onChange={(e) => setNewChannel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newChannel.trim() && !busy) handleCreateChannel();
                  }}
                  placeholder="e.g. MTV Classics"
                  data-testid="new-channel-input"
                  className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] text-[var(--phosphor)] p-2 outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] focus:border-[var(--phosphor)]"
                />
                <button
                  onClick={handleCreateChannel}
                  disabled={busy || !newChannel.trim()}
                  data-testid="create-channel-button"
                  title="Create this channel and make it the active VCR Program Mode selection"
                  className="border border-[var(--phosphor)] px-4 py-2 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[10px] tracking-widest"
                >
                  CREATE CHANNEL
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (workspace === "ingest") {
      return (
        <>
          <WorkspaceHeader
            eyebrow="MANUAL TAPE INGEST"
            title="INGEST"
            description="Link individual or bulk tapes to the active channel from MEGA, YouTube or Internet Archive."
            hint="Manual ingest is for deliberate external tapes. Automatic S4 TV/movie onboarding lives in MEDIA."
          />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
            <div className="xl:col-span-2">
              <MegaIngest {...commonPanelProps} />
            </div>
            <YouTubeIngest {...commonPanelProps} />
            <ArchiveIngest {...commonPanelProps} />
          </div>
        </>
      );
    }

    if (workspace === "media") {
      return (
        <>
          <WorkspaceHeader
            eyebrow="UNIFIED AUTOMATIC MEDIA"
            title="MEDIA"
            description="Automatic S4 TV and movie onboarding, status, review and exceptional repair in one place."
            hint="Normal workflow is folder upload only. Mapping, scanning, TMDb and duration completion are automatic."
          />
          <MediaManagementPanel
            apiBase={apiBase}
            authHeaders={authHeaders}
            channels={channels}
            videosByChannel={videosByChannel}
            selectedChannel={selectedChannel}
            append={append}
            onUploaded={onUploaded}
          />
        </>
      );
    }

    if (workspace === "playlist") {
      return (
        <>
          <WorkspaceHeader
            eyebrow="ACTIVE CHANNEL TAPE MANAGEMENT"
            title="PLAYLIST"
            description="Edit, organise, move, tag, match and audit the tapes belonging to the currently selected channel."
            hint="The channel selector above controls which playlist is shown. Change it without leaving this workspace."
          />
          <PlaylistPanel
            apiBase={apiBase}
            authHeaders={authHeaders}
            channels={channels}
            selectedChannel={selectedChannel}
            channelVideos={channelVideos}
            busy={busy}
            setBusy={setBusy}
            append={append}
            onUploaded={onUploaded}
          />
        </>
      );
    }

    return (
      <>
        <WorkspaceHeader
          eyebrow="VCR PROGRAM MODE"
          title="PROGRAMMING OVERVIEW"
          description="A cleaner command centre for the station. Pick a task instead of scrolling through every programming system at once."
          hint="Overview is navigation-first: click any status card or workflow below to jump directly into that VCR workspace."
        />

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 mb-4">
          <StatCard
            eyebrow="REAL CHANNELS"
            value={realChannels.length}
            detail="Broadcast channels in the current owner lineup."
            onClick={() => setWorkspace("channels")}
            hint="Open CHANNELS to configure lineup order, names, descriptions, artwork and dormant/live state."
            testId="vcr-stat-channels"
          />
          <StatCard
            eyebrow="LIBRARY TAPES"
            value={totalTapes}
            detail="Tape records currently available across the loaded lineup."
            onClick={() => setWorkspace("playlist")}
            hint="Open PLAYLIST for detailed tape management on the currently selected channel."
            testId="vcr-stat-tapes"
          />
          <StatCard
            eyebrow="ACTIVE CHANNEL"
            value={selectedChannelRow?.numberLabel || selectedChannelRow?.number || "—"}
            detail={selectedChannelRow ? `${selectedChannelRow.name} · ${channelVideos.length} tape${channelVideos.length === 1 ? "" : "s"}` : "No channel selected."}
            onClick={() => setWorkspace("playlist")}
            hint="This is the shared active channel used by Channels, Ingest and Playlist."
            testId="vcr-stat-active-channel"
          />
          <StatCard
            eyebrow="ACTIVITY"
            value={log.length}
            detail={latestLog.replace(/^>\s*/, "")}
            onClick={() => setTerminalOpen(true)}
            hint="Open the Terminal drawer to see the complete activity log."
            testId="vcr-stat-activity"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
          {WORKSPACES.filter((item) => item.id !== "overview").map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setWorkspace(item.id)}
              title={item.hint}
              data-testid={`vcr-overview-${item.id}`}
              className="group text-left border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] bg-black/25 p-3 hover:border-[color-mix(in_srgb,var(--phosphor)_62%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_6%,transparent)] transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[9px] tracking-[0.22em] opacity-45 mb-1">OPEN WORKSPACE</div>
                  <div
                    className="text-[22px] tracking-[0.12em]"
                    style={{ fontFamily: "VT323, monospace" }}
                  >
                    {item.label}
                  </div>
                </div>
                <span className="text-[18px] opacity-35 group-hover:opacity-90 group-hover:translate-x-0.5 transition-all">›</span>
              </div>
              <div className="text-[10px] opacity-60 mt-2 leading-relaxed">{item.hint}</div>
            </button>
          ))}
        </div>
      </>
    );
  };

  return (
    <div
      className="absolute inset-0 z-40 bg-black/95 p-3 md:p-5 flex flex-col text-[var(--phosphor)] overflow-hidden"
      style={term}
      data-testid="upload-terminal"
    >
      {/* Compact application header. */}
      <div className="flex items-center justify-between border-b border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] pb-2">
        <div className="min-w-0">
          <div className="text-base md:text-lg uppercase tracking-[0.3em] chromatic truncate">
            ┌─ VCR PROGRAM MODE ─┐
          </div>
          <div className="text-[9px] tracking-[0.22em] opacity-40 mt-0.5 hidden sm:block">
            STATION PROGRAMMING WORKSPACE
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="close-terminal"
          title="Exit VCR Program Mode and return to the CRT"
          className="shrink-0 text-[var(--phosphor)] border border-[var(--phosphor)] px-3 py-1 text-xs hover:bg-[var(--phosphor)] hover:text-black transition-colors"
        >
          [ESC] EXIT
        </button>
      </div>

      {workspace !== "media" && (
        <>
          {/* Shared context strip: channel context is not needed inside MEDIA. */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-2 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] tracking-[0.2em] opacity-45 shrink-0">ACTIVE CHANNEL</span>
          <Hint text="The active channel follows you between CHANNELS, INGEST and PLAYLIST. Automatic S4 onboarding is managed from MEDIA." />
          <select
            value={selectedChannel || ""}
            onChange={(e) => setSelectedChannel(e.target.value)}
            data-testid="vcr-active-channel-select"
            title="Choose the active channel for VCR Program Mode"
            className="min-w-0 max-w-xl bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[10px] outline-none focus:border-[var(--phosphor)]"
          >
            {channels.map((ch) => (
              <option key={ch.id} value={ch.id}>
                {ch.numberLabel || ch.number || "—"} · {ch.name}
              </option>
            ))}
          </select>
          {selectedChannelRow && (
            <span className="hidden xl:inline text-[9px] opacity-45 truncate">
              {channelVideos.length} TAPE{channelVideos.length === 1 ? "" : "S"}
            </span>
          )}
        </div>
        <div className="lg:ml-auto text-[9px] tracking-[0.18em] opacity-45 truncate">
          {busy ? "● WORKING..." : "● SYSTEM READY"}
        </div>
          </div>
        </>
      )}

      {/* Persistent storage health strip. */}
      <div className="pt-2">
        <StorageMeter apiBase={apiBase} authHeaders={authHeaders} refreshKey={channels} append={append} />
      </div>

      {/* Main workspace navigation. Horizontal overflow keeps this usable on
          narrower fullscreen windows without wrapping into a second toolbar. */}
      <div
        className="flex items-center gap-1 overflow-x-auto shrink-0 border-b border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] pb-2 mb-2 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
        data-testid="vcr-workspace-nav"
      >
        {WORKSPACES.map((item) => {
          const active = item.id === workspace;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setWorkspace(item.id)}
              title={item.hint}
              data-testid={`vcr-workspace-${item.id}`}
              className={`shrink-0 px-3 py-1.5 border text-[9px] md:text-[10px] tracking-[0.17em] transition-all ${
                active
                  ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)] text-[var(--phosphor)] shadow-[0_0_10px_color-mix(in_srgb,var(--phosphor)_18%,transparent)]"
                  : "border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] opacity-58 hover:opacity-100 hover:border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)]"
              }`}
            >
              {item.label}
            </button>
          );
        })}

        <div className="ml-auto pl-2 pr-1 shrink-0 hidden md:flex items-center gap-2 opacity-55 text-[9px]">
          {activeWorkspace.label}
          <Hint text={activeWorkspace.hint} />
        </div>
      </div>

      {/* Only the selected programming system is mounted visibly in the main
          canvas. This removes the old left-column "everything at once" stack. */}
      <div
        className="flex-1 min-h-0 overflow-y-auto scrollbar-crt pr-1 text-xs md:text-sm"
        data-testid="vcr-workspace-body"
      >
        {renderWorkspace()}
      </div>

      {/* Terminal is now an activity drawer rather than a permanent 50% column.
          Collapsed state shows the latest event; expand only when diagnostics
          are useful. */}
      <div
        className={`shrink-0 mt-2 border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] bg-black/75 transition-[height] duration-200 overflow-hidden ${
          terminalOpen ? "h-[32vh] min-h-[180px]" : "h-[38px]"
        }`}
        data-testid="vcr-terminal-drawer"
      >
        <button
          type="button"
          onClick={() => setTerminalOpen((v) => !v)}
          data-testid="vcr-terminal-toggle"
          title={terminalOpen ? "Collapse Terminal Output" : "Expand Terminal Output"}
          className="w-full h-[36px] px-3 flex items-center gap-2 text-left hover:bg-[color-mix(in_srgb,var(--phosphor)_6%,transparent)] transition-colors"
        >
          <span className="text-[10px] tracking-[0.2em] shrink-0">
            {terminalOpen ? "▼" : "▶"} TERMINAL
          </span>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${busy ? "bg-[#FFCC00] animate-pulse" : "bg-[var(--phosphor)]"}`} />
          <span className="min-w-0 flex-1 text-[9px] opacity-55 truncate">
            {latestLog}
          </span>
          <span className="text-[9px] opacity-35 shrink-0">{log.length} LINE{log.length === 1 ? "" : "S"}</span>
        </button>

        {terminalOpen && (
          <div className="h-[calc(100%-36px)] border-t border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] overflow-y-auto scrollbar-crt px-3 py-2 text-[10px] md:text-[11px]">
            <div className="flex items-center justify-between gap-3 mb-2 sticky top-0 bg-black/95 py-1">
              <div className="opacity-65 tracking-[0.18em]">┌─ TERMINAL OUTPUT ─┐</div>
              <button
                type="button"
                onClick={() => setLog(["> SYSTEM READY", "> LOG CLEARED."])}
                title="Clear visible Terminal Output. This does not change any station data."
                data-testid="vcr-terminal-clear"
                className="text-[9px] opacity-45 hover:opacity-100 border border-[color-mix(in_srgb,var(--phosphor)_28%,transparent)] px-2 py-0.5"
              >
                CLEAR LOG
              </button>
            </div>
            {log.map((line, i) => (
              <div key={`log-${i}-${(line || "").slice(0, 20)}`} className="leading-relaxed break-words">
                {line}
              </div>
            ))}
            <div className="inline-block w-2 h-3 bg-[var(--phosphor)] animate-pulse ml-1" />
          </div>
        )}
      </div>
    </div>
  );
}
